/**
 * THE ERROR CATALOGUE — every failure the user can see, given a stable name.
 *
 * WHY THIS EXISTS
 * ---------------
 * Until now a failed action produced a sentence built at the call site, and the sentence was
 * the only record of what went wrong:
 *
 *     ToastUtility.show(`Failed to start job. Error: ${error}`, "Error", 2000);
 *
 * That has three problems, and the third is the one this file is for:
 *
 *   1. The operator sees `Error: AxiosError: Request failed with status code 500`, which tells
 *      them nothing and tells support only slightly more.
 *   2. The same failure is worded differently in different files, so it cannot be counted.
 *   3. **The support assistant cannot tell which failure it is looking at.** Asked "why did
 *      this fail", it has to guess from prose that was never written to be matched against.
 *      A near-miss produces a confident answer about a different error entirely.
 *
 * So every user-visible failure now has a CODE. The code is shown to the user, carried into
 * the assistant's question, and used as the `*Also:*` alias of a corpus entry in the Function
 * App's `csm-user-guide.md`. Matching stops being fuzzy prose comparison and becomes an exact
 * token match, which is the whole point: the assistant identifies the error in one step and
 * answers about THAT error, or honestly says it does not know this one.
 *
 * THE RULES
 * ---------
 * - **A code is permanent.** Never renumber, never reuse. A retired code stays here marked
 *   retired, because it is out there in screenshots, e-mails and corpus aliases.
 * - **`operation` is a FIXED label, never interpolated.** It is what the assistant matches and
 *   what a human reads first. Put the variable parts in `context`, not in the label.
 * - **The code goes in the user-visible text.** Support asks for it, the operator reads it out,
 *   and the assistant is handed it verbatim.
 * - **HTTP detail never reaches the model.** `SupportPrompt.cs` forbids the assistant from
 *   mentioning endpoints, payloads or status codes, and it will parrot "a 500 error" at an
 *   operator if you hand it one. Status and endpoint travel in `diagnostics`, which goes to the
 *   escalation e-mail and nowhere else.
 *
 * @see src/utility/reportError.ts — the single reporting path that uses this table.
 * @see Support/csm-user-guide.md in the Function App — one corpus entry per code.
 */

/** What the user must do next. Drives the wording and whether escalation is offered. */
export type ErrorDisposition =
  /** Almost always transient. Worth trying again before anything else. */
  | "retry"
  /** The user did something the app does not allow. There is a correct action available. */
  | "user-correctable"
  /** A permission or configuration gap. Someone else has to change something. */
  | "needs-someone-else"
  /** Nothing the user can do. Support needs to see it. */
  | "report";

export interface ErrorDescriptor {
  /** `CSM-AREA-NNN`. Permanent. Appears in the UI, the corpus and support e-mail. */
  readonly code: string;
  /** Fixed human label for what was being attempted. NEVER interpolated. */
  readonly operation: string;
  /** What the assistant should be asked. `{code}` is substituted; nothing else is. */
  readonly question: string;
  readonly disposition: ErrorDisposition;
}

const d = (
  code: string,
  operation: string,
  disposition: ErrorDisposition,
  question?: string
): ErrorDescriptor => ({
  code,
  operation,
  disposition,
  // The default question is deliberately boring and uniform: uniform phrasing is what makes
  // the corpus alias match reliably. Override only when a failure has a genuinely different
  // shape of question behind it.
  question: question ?? `I got error ${code} in CSM when I tried to ${operation.toLowerCase()}. What does that mean and what should I do?`,
});

/**
 * A REFUSAL descriptor. Same table, same rules, one difference in how it is used.
 *
 * `operation` on a normal descriptor is what the user was ATTEMPTING. On a refusal it is what
 * the server REFUSED — so it must never be substituted into the "when I tried to …" template,
 * which would produce "when I tried to work a resource that is not on your list". The refusal's
 * code replaces the attempt's code; the attempt's own `operation` still supplies the
 * "What I was doing" line. See `buildReportedError`.
 */
const r = (code: string, operation: string, disposition: ErrorDisposition): ErrorDescriptor =>
  d(code, operation, disposition, `I got error ${code} in CSM. What does that mean and what should I do?`);

/**
 * JOB — starting, stopping and moving work through a production order.
 * These are the failures an operator hits mid-shift, so they matter most.
 */
export const JOB_ERRORS = Object.freeze({
  START: d("CSM-JOB-001", "Start a job", "retry"),
  START_ALREADY_RUNNING: d("CSM-JOB-002", "Start a job while another is running", "user-correctable"),
  STOP: d("CSM-JOB-003", "Stop a job", "retry"),
  STOP_NOTHING_STARTED: d("CSM-JOB-004", "Stop a job that was never started", "user-correctable"),
  STOP_OTHER_JOB: d("CSM-JOB-005", "Stop a job you are not logged into", "user-correctable"),
  REWORK: d("CSM-JOB-006", "Send a resource back for rework", "retry"),
  REWORK_NOTE: d("CSM-JOB-007", "Add the rework note", "retry"),
  REWORK_REASONS: d("CSM-JOB-008", "Load the reasons for rework", "needs-someone-else"),
  REWORK_NOTIFY: d("CSM-JOB-009", "Send the rework notification", "report"),
  REWORK_BP_DETAILS: d("CSM-JOB-010", "Load contact details for the rework notification", "report"),
  COMPLETE_RESOURCE: d("CSM-JOB-011", "Complete a resource", "retry"),
  COMPLETE_OUT_OF_SEQUENCE: d("CSM-JOB-012", "Complete a resource out of sequence", "user-correctable"),
  NOTE_ADD: d("CSM-JOB-013", "Add a note to a job", "retry"),
  NOTE_LOAD: d("CSM-JOB-014", "Load the notes for a job", "retry"),
  LABEL_PRINT: d("CSM-JOB-015", "Print a label", "retry"),
  DELETE_OPERATION: d("CSM-JOB-016", "Delete a logged resource", "retry"),
  CARD_LOAD: d("CSM-JOB-017", "Load the job card", "retry"),
  REFRESH: d("CSM-JOB-018", "Refresh a job", "retry"),
  UPDATE: d("CSM-JOB-019", "Update a job", "retry"),
  STARTED_JOB_LOAD: d("CSM-JOB-020", "Check which job you are logged into", "retry"),
  LOGGED_HOURS_REFRESH: d("CSM-JOB-021", "Refresh who is logged on to a job", "retry"),
  // A workflow rule (executeWorkflow → "SendEmail") asks the BFF to send a notification
  // mid-action. The operator did nothing wrong and can do nothing about it, but the action
  // they ran is now missing the notification somebody downstream is waiting for.
  WORKFLOW_EMAIL: d("CSM-JOB-022", "Send an e-mail from a resource workflow step", "report"),
  // 023–028 are the refusals below. Start Job and Complete Job ask which toolsets a resource
  // offers before they draw the dropdown; this is that read failing.
  JOB_TOOLSETS_LOAD: d("CSM-JOB-029", "Load the toolsets for a job", "retry"),
});

/**
 * REFUSALS — the server said no ON PURPOSE, and said why.
 *
 * WHY THESE ARE SEPARATE FROM THE CODES ABOVE
 * -------------------------------------------
 * Every code above names an ACTION that failed. That was enough while the Function App
 * answered every failed job action with one sentence — "The request could not be processed."
 * — because the action really was all anybody knew.
 *
 * It was not all the Function App knew. `JobActionService` has always written the real reason
 * ("You can't start ASSEMBLY operation. The operation is not allowed") and then thrown it as a
 * bare `Exception` that every endpoint caught and replaced. So an operator refused permission
 * to start a resource got CSM-JOB-001 — whose disposition is `retry`, whose corpus entry says
 * "these mean the request did not get through, not that you did anything wrong" — and was told
 * by the assistant to try again. Confidently, and twice, because that answer was correct for
 * the code it was given and the code was wrong.
 *
 * The endpoints now return `{ reason, message }` for a refusal (`ActionRefusedException.cs`),
 * and the slug resolves HERE to a code that means the refusal rather than the attempt. The
 * assistant then matches an exact token again, which is the whole premise of this file.
 *
 * ⚠️ `REFUSAL_BY_REASON` IS HALF OF A CROSS-REPOSITORY CONTRACT. The slugs are
 * `RefusalReasons` in the Function App's `Services/ActionRefusedException.cs`. A slug that does
 * not resolve here is not an error — the failure keeps the generic code for the action, which
 * is exactly the old behaviour — so a typo is SILENT, and the only symptom is the assistant
 * going back to answering about the wrong thing.
 */
export const REFUSAL_ERRORS = Object.freeze({
  /** The resource is not on the person's list of resources they may log time to. */
  RESOURCE_NOT_ALLOWED: r("CSM-JOB-023", "Work a resource that is not on your list", "needs-someone-else"),
  /** Assigned, but a required training is missing or has expired. A different person fixes each. */
  TRAINING_OUTSTANDING: r("CSM-JOB-024", "Work a resource with training outstanding", "needs-someone-else"),
  RESOURCE_ALREADY_COMPLETED: r("CSM-JOB-025", "Start a resource that is already completed", "user-correctable"),
  /** WELD INSP after WELD, FINAL INSP after ASSEMBLY, and so on: you cannot inspect your own work. */
  INSPECTION_SEGREGATION: r("CSM-JOB-026", "Inspect work you did yourself", "user-correctable"),
  REWORK_NOT_ALLOWED: r("CSM-JOB-027", "Send back for rework a resource you are not allowed to", "needs-someone-else"),
  REWORK_NOT_COMPLETED: r("CSM-JOB-028", "Send back for rework a resource that was never completed", "user-correctable"),
});

/**
 * Function App refusal slug → the code that refusal is reported under.
 *
 * `out-of-sequence` deliberately resolves to the EXISTING CSM-JOB-012 rather than a new code:
 * the app already refuses that one client-side under that code when it can see the routings,
 * and the same refusal reaching the user under two different codes depending on which side
 * caught it is precisely the ambiguity this table exists to remove.
 */
export const REFUSAL_BY_REASON: ReadonlyMap<string, ErrorDescriptor> = new Map([
  ["resource-not-allowed", REFUSAL_ERRORS.RESOURCE_NOT_ALLOWED],
  ["training-outstanding", REFUSAL_ERRORS.TRAINING_OUTSTANDING],
  ["resource-already-completed", REFUSAL_ERRORS.RESOURCE_ALREADY_COMPLETED],
  ["inspection-segregation", REFUSAL_ERRORS.INSPECTION_SEGREGATION],
  ["out-of-sequence", JOB_ERRORS.COMPLETE_OUT_OF_SEQUENCE],
  ["rework-not-allowed", REFUSAL_ERRORS.REWORK_NOT_ALLOWED],
  ["rework-not-completed", REFUSAL_ERRORS.REWORK_NOT_COMPLETED],
]);

/** Resolve a refusal slug. An unknown one returns null and the caller keeps its own code. */
export const refusalFor = (reason: string | null | undefined): ErrorDescriptor | null =>
  (reason && REFUSAL_BY_REASON.get(reason.trim().toLowerCase())) || null;

/** PRODUCTION ORDER — the manager-side completion and close flow. */
export const ORDER_ERRORS = Object.freeze({
  COMPLETE: d("CSM-PO-001", "Complete a production order", "retry"),
  UNCOMPLETE: d("CSM-PO-002", "Un-complete a production order", "report"),
  CLOSE_RECORD: d("CSM-PO-003", "Record the close on a completed order", "report"),
  LOCK_RELEASE: d("CSM-PO-004", "Release the completion lock on an order", "report"),
  LOCK_HELD: d("CSM-PO-005", "Complete an order someone else is completing", "user-correctable"),
  ISSUE: d("CSM-PO-006", "Issue the parts for a production order", "retry"),
  REPORT: d("CSM-PO-007", "Report a production order as complete", "retry"),
  WRITEBACK: d("CSM-PO-008", "Send a completion back to the ERP", "report"),
  // Refused before anything is sent to SAP: the order's completed quantity is below its
  // planned quantity, so closing it would strand the balance. The operator fixes it by
  // reporting the rest of the quantity first.
  CLOSE_QUANTITY_SHORT: d("CSM-PO-009", "Close an order that is not fully completed", "user-correctable"),
});

/** DATA — read failures. Individually dull, collectively the commonest thing users see. */
export const DATA_ERRORS = Object.freeze({
  JOBS: d("CSM-DATA-001", "Load the jobs list", "retry"),
  JOB_DETAILS: d("CSM-DATA-002", "Load the production order details", "retry"),
  BOM: d("CSM-DATA-003", "Load the parts list", "retry"),
  BOM_UPDATE: d("CSM-DATA-004", "Save a change to the parts list", "retry"),
  ROUTING: d("CSM-DATA-005", "Load the resources list", "retry"),
  OPERATIONS: d("CSM-DATA-006", "Load the resources", "retry"),
  OPERATION_FILTERS: d("CSM-DATA-007", "Load the resource filters", "retry"),
  RESOURCE_FILTERS: d("CSM-DATA-008", "Load the resource filters", "retry"),
  WAREHOUSES: d("CSM-DATA-009", "Load the warehouses", "retry"),
  SIMILAR_JOBS: d("CSM-DATA-010", "Load similar jobs", "retry"),
  PURCHASE_ORDERS: d("CSM-DATA-011", "Load the purchase orders", "retry"),
  BANK_HOLIDAYS: d("CSM-DATA-012", "Load the bank holidays", "retry"),
  GENERIC: d("CSM-DATA-013", "Load data", "retry"),
  SEARCH: d("CSM-DATA-014", "Search for production orders", "retry"),
  // The manual "Import" / resync that pulls SAP data into the bridge. Only the REQUEST to
  // start it can fail here — the import itself runs server-side and reports separately.
  RESYNC: d("CSM-DATA-015", "Start a manual data import", "retry"),
});

/** IMAGES AND DOCUMENTS. */
export const FILE_ERRORS = Object.freeze({
  IMAGES_LOAD: d("CSM-FILE-001", "Load the images", "retry"),
  IMAGE_CONTENT: d("CSM-FILE-002", "Open an image", "retry"),
  IMAGE_UPLOAD: d("CSM-FILE-003", "Add an image", "retry"),
  IMAGE_DELETE: d("CSM-FILE-004", "Delete an image", "retry"),
  DOCUMENTS_LOAD: d("CSM-FILE-005", "Load the documents", "retry"),
  DOCUMENT_UPLOAD: d("CSM-FILE-006", "Upload a document", "retry"),
  DOCUMENT_OPEN: d("CSM-FILE-007", "Open a document", "retry"),
  DOCUMENT_DELETE: d("CSM-FILE-008", "Delete a document", "retry"),
  PROJECT_FOLDER: d("CSM-FILE-009", "Find the project folder", "needs-someone-else"),
});

/** INSPECTIONS AND CERTIFICATES. */
export const INSPECTION_ERRORS = Object.freeze({
  TOOLSETS: d("CSM-INSP-001", "Load the inspection toolsets", "needs-someone-else"),
  REASONS_ACCEPT: d("CSM-INSP-002", "Load the reasons to accept", "needs-someone-else"),
  REASONS_REJECT: d("CSM-INSP-003", "Load the reasons to reject", "needs-someone-else"),
  SAVE: d("CSM-INSP-004", "Save an inspection", "retry"),
  CERTIFICATE_SAVE: d("CSM-INSP-005", "Save a test certificate", "retry"),
  CERTIFICATE_PRINT: d("CSM-INSP-006", "Print a certificate", "retry"),
  // The barcode scanner handed back something that is not a production order number. The
  // only failure here the operator can fix on the spot, hence user-correctable.
  SCANNED_ORDER_INVALID: d("CSM-INSP-007", "Scan a production order barcode", "user-correctable"),
  REPORT_LOAD: d("CSM-INSP-008", "Open an inspection report", "retry"),
  REPORTS_LOAD: d("CSM-INSP-009", "Load the inspection reports", "retry"),
  REPORT_UNLOCK: d("CSM-INSP-010", "Unlock an inspection report", "retry"),
  REPORT_EMAIL: d("CSM-INSP-011", "Send the inspection report", "report"),
  // The certificates/toolsets/printer read the leak-test and press-test panels do on open.
  // It is one fetch chain, so one code: a partial failure leaves the panel unusable either way.
  CERTIFICATE_DATA: d("CSM-INSP-012", "Load the test certificate data", "retry"),
});

/** SETTINGS — users, resources and system options. Manager-facing. */
export const SETTINGS_ERRORS = Object.freeze({
  USER_SAVE: d("CSM-SET-001", "Save a user", "retry"),
  USER_LOAD: d("CSM-SET-002", "Load the users", "retry"),
  RESOURCE_SAVE: d("CSM-SET-003", "Save a resource", "retry"),
  OPTION_LOAD: d("CSM-SET-004", "Read a system setting", "retry"),
  OPTION_SAVE: d("CSM-SET-005", "Save a system setting", "retry"),
  PREFERENCE_LOAD: d("CSM-SET-006", "Load your preferences", "retry"),
  PREFERENCE_SAVE: d("CSM-SET-007", "Save your preferences", "retry"),
  // Separate codes rather than one "save failed": these are three different tabs of the
  // manager settings screens, and support's first question is always which one.
  REWORK_REASONS_SAVE: d("CSM-SET-008", "Save the reasons for rework", "retry"),
  RESOURCE_FILTERS_SAVE: d("CSM-SET-009", "Save the resource filters", "retry"),
  WAREHOUSES_SAVE: d("CSM-SET-010", "Save the warehouses", "retry"),
  // CSM-SET-011 to CSM-SET-016 are set aside for the certifications screens, which are not
  // built yet. Leave the gap: a code is permanent, so the numbers cannot be handed back later.
  //
  // The Configuration tab of Tools and Toolsets. The read and the write have a code each
  // because they leave the manager in different places: a failed read shows no list at all, a
  // failed save keeps the list that was on screen.
  OOS_REASONS_LOAD: d("CSM-SET-017", "Load the out-of-service reasons", "retry"),
  OOS_REASONS_SAVE: d("CSM-SET-018", "Save the out-of-service reasons", "retry"),
  // The Tools tab of Tools and Toolsets: one code for reading the list, one for a write to a
  // tool.
  TOOLS_LOAD: d("CSM-SET-019", "Load the tools", "retry"),
  TOOL_SAVE: d("CSM-SET-020", "Save a tool", "retry"),
  // The Toolsets tab of Tools and Toolsets: one code for reading the list, one for a write to a
  // toolset, which is a save or a delete.
  TOOLSETS_LOAD: d("CSM-SET-021", "Load the toolsets", "retry"),
  TOOLSET_SAVE: d("CSM-SET-022", "Save or delete a toolset", "retry"),
  // A tool's calibration history, opened from the Tools tab.
  CALIBRATIONS_LOAD: d("CSM-SET-023", "Load a tool's calibration history", "retry"),
  CALIBRATION_SAVE: d("CSM-SET-024", "Save a calibration", "retry"),
  CERTIFICATE_FILE: d("CSM-SET-025", "Upload or download a calibration certificate", "retry"),
});

/** CAPACITY AND PLANNING. */
export const CAPACITY_ERRORS = Object.freeze({
  LOAD: d("CSM-CAP-001", "Load the capacity view", "retry"),
  DUE_DATE_SAVE: d("CSM-CAP-002", "Change a due date", "retry"),
  ALLOCATIONS: d("CSM-CAP-003", "Load the allocations", "retry"),
});

/** REPORTS. */
export const REPORT_ERRORS = Object.freeze({
  RUN: d("CSM-RPT-001", "Run a report", "retry"),
  SAVE: d("CSM-RPT-002", "Save a report", "retry"),
  SHARE: d("CSM-RPT-003", "Share a report", "retry"),
  EMPTY: d("CSM-RPT-004", "Run a report that came back empty", "user-correctable"),
  SCHEMA: d("CSM-RPT-005", "Load the fields of a table in the report builder", "retry"),
  // Not a network failure: the stored definition itself will not parse, so retrying is
  // pointless and the report has to be rebuilt or repaired by support.
  OPEN: d("CSM-RPT-006", "Open a saved report", "report"),
  DELETE: d("CSM-RPT-007", "Delete a saved report", "retry"),
});

/** SIGN-IN AND ACCESS. */
export const ACCESS_ERRORS = Object.freeze({
  SIGN_IN: d("CSM-ACC-001", "Sign in", "needs-someone-else"),
  DENIED: d("CSM-ACC-002", "Open something you do not have access to", "needs-someone-else"),
  SESSION_EXPIRED: d("CSM-ACC-003", "Continue after your session expired", "retry"),
  TENANT: d("CSM-ACC-004", "Switch organisation", "needs-someone-else"),
});

/** Every descriptor, flattened. Used by the contract tests and the corpus check. */
export const ALL_ERRORS: readonly ErrorDescriptor[] = Object.freeze([
  ...Object.values(JOB_ERRORS),
  ...Object.values(REFUSAL_ERRORS),
  ...Object.values(ORDER_ERRORS),
  ...Object.values(DATA_ERRORS),
  ...Object.values(FILE_ERRORS),
  ...Object.values(INSPECTION_ERRORS),
  ...Object.values(SETTINGS_ERRORS),
  ...Object.values(CAPACITY_ERRORS),
  ...Object.values(REPORT_ERRORS),
  ...Object.values(ACCESS_ERRORS),
]);

/**
 * `CSM-AREA-NNN`. Exported so the Function App's contract test can restate it.
 *
 * ⚠️ TWO letters, not three. The production-order area is `PO`, so a `{3,4}` bound excluded
 * all eight `CSM-PO-NNN` codes — and since `findErrorCode` is built from the same bound,
 * pasting "CSM-PO-005" into the chat resolved to nothing at all. Widen this and the
 * matcher together, never one alone.
 */
export const ERROR_CODE_SHAPE = /^CSM-[A-Z]{2,4}-\d{3}$/;

const BY_CODE: ReadonlyMap<string, ErrorDescriptor> = new Map(ALL_ERRORS.map((e) => [e.code, e]));

/** Look a descriptor up by code. Unknown codes return null; they never throw. */
export const errorFor = (code: string | null | undefined): ErrorDescriptor | null =>
  (code && BY_CODE.get(code.trim().toUpperCase())) || null;

/**
 * Pull a code out of arbitrary text — a toast the user copied, a subject line, whatever they
 * pasted into the chat. This is what lets someone type "what is CSM-JOB-006" and get a real
 * answer rather than a guess.
 */
export const findErrorCode = (text: string | null | undefined): ErrorDescriptor | null => {
  if (!text) return null;
  const match = text.toUpperCase().match(/CSM-[A-Z]{2,4}-\d{3}/);
  return match ? errorFor(match[0]) : null;
};
